import React, { ButtonHTMLAttributes, MutableRefObject } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import media from '../../constants/media';
import theme from '../../constants/theme';
import SVG from '../rawSVG';

const ButtonIconWrapper = styled.span`
	margin-bottom: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	transition: 0.3s ease;

	svg path {
		stroke: ${({ disabled }) => !disabled && theme.colorsV2.mainRed};
	}

	${media.tablet`
		margin-bottom: 0;
		margin-right: 16px;
	`};
`;

const ButtonTitle = styled.span`
	max-width: 100%;
	color: ${theme.colorsV2.mainDark};
	font-family: ${theme.fonts.Arimo};
	font-size: 16px;
	line-height: 1.3;
	text-align: center;
	overflow-wrap: break-word;

	${media.tablet`
		max-width: calc(100% - 40px);
	`};

	${media.desktopSmall`
		font-size: 18px;
		line-height: 1.4;
	`};
`;

interface ButtonRootProps {
	disabled?: boolean;
	iconRight?: boolean;
	primary: boolean;
	hidden?: boolean;
	$shrinkForMobile?: boolean;
}

const ButtonRoot = styled.button<ButtonRootProps>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	padding: 8px;
	background-color: ${theme.colors.white};
	cursor: ${({ disabled }) => !disabled && 'pointer'};
	transition: all 0.3s ease;
	box-shadow: 0 0 30px rgba(0, 0, 0, 0.08);

	${ButtonTitle} {
		color: ${({ disabled }) => disabled && theme.colorsV2.grey6};
	}

	svg path {
		stroke: ${({ disabled }) => disabled && theme.colorsV2.grey6};
	}

	&:active {
		${ButtonTitle} {
			color: ${({ disabled }) => !disabled && theme.colorsV2.lightRed};
		}

		svg path {
			stroke: ${({ disabled }) => !disabled && theme.colorsV2.lightRed};
		}
	}

	&:focus-visible {
		box-shadow: 0 0 50px rgba(0, 0, 0, 0.15);
		outline: none;
	}

	${media.tablet`
		padding: 24px 18px;
		flex-direction: row;
    `};

	${media.desktopSmall`
		&:hover {
			box-shadow: ${({ disabled }) => !disabled && '0 0 50px rgba(0, 0, 0, 0.15)'};
		}
	`};

	${({ $shrinkForMobile }) =>
		!$shrinkForMobile &&
		css`
			padding: 24px 18px;
			flex-direction: row;

			${ButtonIconWrapper} {
				margin: 0 16px 0 0;
			}

			${ButtonTitle} {
				max-width: calc(100% - 40px);
				font-size: 18px;
				line-height: 1.4;
			}
		`}
`;

type TProps = {
	buttonRef?: MutableRefObject<HTMLButtonElement | null>;
	iconUrl: string;
	title: string;
	href?: string;
	target?: string | null;
	onClick?: () => void;
	htmlType?: ButtonHTMLAttributes<any>['type'];
	disabled?: boolean;
	hidden?: boolean;
	shrinkForMobile?: boolean;
	className?: string;
};

const IconButtonWithText = ({
	buttonRef,
	iconUrl,
	title,
	href,
	target,
	onClick,
	htmlType,
	disabled,
	hidden,
	shrinkForMobile,
	className,
}: TProps) => {
	if (!href) {
		return (
			<ButtonRoot
				ref={buttonRef}
				onClick={!disabled && onClick ? onClick : undefined}
				type={htmlType}
				disabled={disabled}
				aria-hidden={hidden}
				aria-label={title}
				tabIndex={hidden ? -1 : 0}
				$shrinkForMobile={shrinkForMobile}
				className={className}>
				<ButtonIconWrapper>
					<SVG src={iconUrl} width={24} height={24} />
				</ButtonIconWrapper>
				<ButtonTitle aria-hidden="true">{title}</ButtonTitle>
			</ButtonRoot>
		);
	}
	return (
		<ButtonRoot
			{...(!href.startsWith('http') &&
				(!href.endsWith('pdf') ||
					!href.endsWith('exe') ||
					!href.endsWith('zip') ||
					!href.endsWith('xls') ||
					!href.endsWith('xlsx')) && {
					as: Link,
					to: href,
				})}
			{...((href.startsWith('http') ||
				href.endsWith('pdf') ||
				href.endsWith('exe') ||
				href.endsWith('zip') ||
				href.endsWith('xls') ||
				href.endsWith('xlsx')) && {
				as: 'a',
				href: href,
				target: target,
			})}
			ref={buttonRef}
			onClick={!disabled ? onClick : undefined}
			disabled={disabled}
			aria-hidden={hidden}
			aria-label={title}
			tabIndex={hidden ? -1 : 0}
			$shrinkForMobile={shrinkForMobile}
			className={className}>
			<ButtonIconWrapper>
				<SVG src={iconUrl} width={24} height={24} />
			</ButtonIconWrapper>
			<ButtonTitle aria-hidden="true">{title}</ButtonTitle>
		</ButtonRoot>
	);
};

export default IconButtonWithText;

IconButtonWithText.defaultProps = {
	disabled: false,
	hidden: false,
	shrinkForMobile: false,
	href: null,
	target: null,
	onClick: null,
	htmlType: 'button',
	buttonRef: null,
	className: '',
};
